import {
    Drawer,
    DrawerOverlay,
    DrawerContent,
    DrawerFooter,
    HStack,
    Box,
    Text,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Input,
    useToast,
    Spinner,
    Flex,
    Grid,
    Collapse,
    useDisclosure,
    Tooltip,
    CloseButton,
    Switch,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    MenuDivider,
    Portal,
} from '@chakra-ui/react';
import {
    Button,
    ConfirmDialog,
    Dropdown,
    Icon,
    IconButton,
    Modal,
    StatusBadge,
    type StatusTone,
} from '../../ui';
import { useState, useEffect, useRef, useMemo } from 'react';
import moment from 'moment';
import { useQueryClient } from '@tanstack/react-query';
import { useForceSidebarCollapsed } from '../../contexts/SidebarCollapseOverrideContext';
import { useGetInventoryItem } from '../../hooks/inventory/useGetInventoryItem';
import { useUpdateInventoryItem } from '../../hooks/inventory/useUpdateInventoryItem';
import { useGetInventoryStockAdjustments } from '../../hooks/inventory/useGetInventoryStockAdjustments';
import { useCreateInventoryStockAdjustment } from '../../hooks/inventory/useCreateInventoryStockAdjustment';
import { useDeleteInventoryStockAdjustment } from '../../hooks/inventory/useDeleteInventoryStockAdjustment';
import { useUpdateInventoryStockAdjustmentNote } from '../../hooks/inventory/useUpdateInventoryStockAdjustmentNote';
import { useSearchSuppliers } from '../../hooks/useSearchSuppliers';
import { filterLocations, useGetLocations } from '../../hooks/useGetLocations';
import {
    InventoryBin,
    InventoryItemEntry,
    InventoryStatus,
    InventoryTag,
    UpdateInventoryItemInput,
} from '../../types/inventory';
import {
    INVENTORY_CATEGORY_OPTIONS,
    formatInventoryPartTypeLabel,
    formatInventoryUnitLabel,
    parseInventoryDuplicateError,
    parseInventoryReferenceValidationError,
} from '../../utils/inventoryConstants';
import CopyButton from '../../components/CopyButton';
import InventoryPartNumbersField from '../../components/inventory/InventoryPartNumbersField';
import InventoryTypeaheadField, {
    TypeaheadOption,
} from '../../components/inventory/InventoryTypeaheadField';
import QuantityInputField from '../../components/inventory/QuantityInputField';
import InventoryItemImageField from '../../components/inventory/InventoryItemImageField';
import InventoryTagsField from '../../components/inventory/InventoryTagsField';
import InventoryBinsField from '../../components/inventory/InventoryBinsField';
import { useUploadInventoryImage } from '../../hooks/inventory/useUploadInventoryImage';
import { useCreateInventoryTag } from '../../hooks/inventory/useCreateInventoryTag';
import { useCreateInventoryBin } from '../../hooks/inventory/useCreateInventoryBin';
import { InventoryItemActivityPanel } from './InventoryItemActivityDrawer';
import InventoryStockAdjustmentsSection, {
    COLLAPSED_LIMIT as STOCK_ADJUSTMENTS_COLLAPSED_LIMIT,
    PendingStockAdjustmentCreate,
    PendingStockAdjustmentNoteUpdates,
} from '../../components/inventory/InventoryStockAdjustmentsSection';
import { formatPrice } from '../../functions';
import { formatPartNumber } from '../../functions/formatPartNumber';
import {
    PartNumberEditorRow,
    editorRowsFingerprint,
    equivalentPartNumbersEqual,
    equivalentPartNumbersToEditorRows,
    getPrimaryFromEditorRows,
    getPrimaryPartNumber,
    normalizePartNumberRows,
} from '../../utils/inventoryPartNumbers';

interface EditFormState {
    name: string;
    supplierId: string;
    locationId: string;
    restockAlert: boolean;
    reorderQuantity: number | null;
    targetQuantity: number | null;
    status: InventoryStatus;
}

function itemToForm(item: InventoryItemEntry): EditFormState {
    return {
        name: item.name,
        supplierId: item.supplierId,
        locationId: item.locationId,
        restockAlert: item.restockAlert ?? false,
        reorderQuantity: item.reorderQuantity,
        targetQuantity: item.targetQuantity,
        status: item.status,
    };
}

function formsEqual(a: EditFormState, b: EditFormState): boolean {
    return JSON.stringify(a) === JSON.stringify(b);
}

function buildChanges(
    form: EditFormState,
    saved: EditFormState,
): UpdateInventoryItemInput['changes'] {
    const changes: UpdateInventoryItemInput['changes'] = {};

    if (form.name !== saved.name) changes.name = form.name;
    if (form.supplierId !== saved.supplierId) changes.supplierId = form.supplierId;
    if (form.locationId !== saved.locationId) changes.locationId = form.locationId;
    if (form.restockAlert !== saved.restockAlert) {
        changes.restockAlert = form.restockAlert;
    }
    if (form.reorderQuantity !== saved.reorderQuantity) {
        changes.reorderQuantity = form.reorderQuantity;
    }
    if (form.targetQuantity !== saved.targetQuantity) {
        changes.targetQuantity = form.targetQuantity;
    }
    if (form.status !== saved.status) changes.status = form.status;

    return changes;
}

interface EditInventoryItemDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    inventoryItemId: string | null;
    onDelete?: (item: InventoryItemEntry) => void;
}

type EditableCardKey = 'reorderQuantity' | 'targetQuantity' | 'supplierId' | 'locationId' | 'name';

const EDIT_DRAWER_BASE_MAX_W = '60rem';
const ACTIVITY_PANEL_W = '418px';
const EDIT_DRAWER_WITH_ACTIVITY_MAX_W = `calc(60rem + ${ACTIVITY_PANEL_W})`;
const IMAGE_TILE_SIDE = '128px';

const statLabelProps = {
    fontSize: 'xs',
    fontWeight: 'semibold',
    color: 'fg.muted',
    letterSpacing: 'wide',
    textTransform: 'uppercase',
} as const;

const StatCell = ({
    label,
    index,
    children,
    onEdit,
    cellRef,
}: {
    label: string;
    index: number;
    children: React.ReactNode;
    onEdit?: () => void;
    cellRef?: React.Ref<HTMLDivElement>;
}) => (
    <Box
        ref={cellRef}
        py={4}
        pl={{ base: index % 2 ? 4 : 0, md: index ? 4 : 0 }}
        pr={4}
        borderLeftWidth={{ base: index % 2 ? '1px' : 0, md: index ? '1px' : 0 }}
        borderTopWidth={{ base: index > 1 ? '1px' : 0, md: 0 }}
        borderColor="border.subtle"
        minW={0}
    >
        <Flex justify="space-between" align="center" mb={1.5} minH="20px">
            <Text {...statLabelProps}>{label}</Text>
            {onEdit && (
                <Tooltip label={`Edit ${label.toLowerCase()}`} placement="top">
                    <IconButton
                        aria-label={`Edit ${label.toLowerCase()}`}
                        icon={<Icon name="pencil" size="sm" />}
                        size="xs"
                        variant="ghost"
                        colorScheme="gray"
                        color="fg.muted"
                        minW="auto"
                        h="auto"
                        p={1}
                        onClick={onEdit}
                    />
                </Tooltip>
            )}
        </Flex>
        {children}
    </Box>
);

const DetailField = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <Box minW={0}>
        <Text {...statLabelProps} mb={1}>
            {label}
        </Text>
        <Text fontSize="sm" fontWeight="medium" color="fg.default" noOfLines={1}>
            {value}
        </Text>
    </Box>
);

const getItemStatusMeta = (
    status: InventoryStatus,
    inStockQuantity: number,
    isLowStock: boolean,
): { tone: StatusTone; label: string } => {
    if (status === 'discontinued') return { tone: 'neutral', label: 'Discontinued' };
    if (inStockQuantity <= 0) return { tone: 'error', label: 'Out of stock' };
    if (isLowStock) return { tone: 'warning', label: 'Low stock' };
    return { tone: 'success', label: 'In stock' };
};

const ItemStatusBadge = ({
    status,
    inStockQuantity,
    isLowStock,
}: {
    status: InventoryStatus;
    inStockQuantity: number;
    isLowStock: boolean;
}) => {
    const { tone, label } = getItemStatusMeta(status, inStockQuantity, isLowStock);
    return (
        <StatusBadge
            tone={tone}
            display="inline-flex"
            alignItems="center"
            gap={1.5}
            fontSize="xs"
        >
            <Box as="span" w="6px" h="6px" borderRadius="full" bg="currentColor" />
            {label}
        </StatusBadge>
    );
};

const StatusDot = ({
    status,
    isFlagged = false,
}: {
    status: InventoryStatus;
    isFlagged?: boolean;
}) => {
    let bg = 'fg.subtle';
    if (status === 'active') {
        bg = isFlagged ? 'warning.solid' : 'success.solid';
    }
    return (
        <Box w="10px" h="10px" borderRadius="full" bg={bg} flexShrink={0} />
    );
};

const StatusBadgeMenu = ({
    value,
    inStockQuantity,
    isFlagged = false,
    onChange,
    onDelete,
}: {
    value: InventoryStatus;
    inStockQuantity: number;
    isFlagged?: boolean;
    onChange: (status: InventoryStatus) => void;
    onDelete?: () => void;
}) => (
    <Menu placement="bottom-start">
        <MenuButton
            aria-label={`Status: ${getItemStatusMeta(value, inStockQuantity, isFlagged).label}`}
            borderRadius="full"
            flexShrink={0}
            transition="opacity 0.15s ease"
            _hover={{ opacity: 0.8 }}
            _focusVisible={{ boxShadow: 'outline' }}
        >
            <ItemStatusBadge
                status={value}
                inStockQuantity={inStockQuantity}
                isLowStock={isFlagged}
            />
        </MenuButton>
        <Portal>
            <MenuList minW="180px" py={1} boxShadow="none" zIndex={1500}>
                {(
                    [
                        { value: 'active' as const, label: 'Active' },
                        { value: 'discontinued' as const, label: 'Discontinued' },
                    ] as const
                ).map((option) => {
                    const isSelected = value === option.value;
                    return (
                        <MenuItem
                            key={option.value}
                            onClick={() => {
                                if (!isSelected) onChange(option.value);
                            }}
                            fontSize="sm"
                            px={3}
                            py={2}
                        >
                            <HStack spacing={3} w="100%">
                                <StatusDot
                                    status={option.value}
                                    isFlagged={
                                        option.value === 'active' ? isFlagged : false
                                    }
                                />
                                <Text flex={1}>{option.label}</Text>
                                {isSelected && (
                                    <Icon name="check" size="md" color="brand.fg" />
                                )}
                            </HStack>
                        </MenuItem>
                    );
                })}
                {onDelete && (
                    <>
                        <MenuDivider />
                        <MenuItem
                            onClick={onDelete}
                            fontSize="sm"
                            px={3}
                            py={2}
                            color="error.fg"
                            fontWeight="medium"
                            _hover={{ bg: 'error.subtle' }}
                        >
                            <HStack spacing={3} w="100%">
                                <Icon name="trash" size="md" color="error.fg" />
                                <Text flex={1}>Delete</Text>
                            </HStack>
                        </MenuItem>
                    </>
                )}
            </MenuList>
        </Portal>
    </Menu>
);

const EditInventoryItemDrawer = ({
    isOpen,
    onClose,
    inventoryItemId,
    onDelete,
}: EditInventoryItemDrawerProps) => {
    useForceSidebarCollapsed(isOpen);
    const toast = useToast();
    const queryClient = useQueryClient();
    const supplierCardRef = useRef<HTMLDivElement>(null);
    const detailsRef = useRef<HTMLDivElement>(null);
    const bodyRef = useRef<HTMLDivElement>(null);
    const blockScrollRef = useRef(false);
    const preEditFieldRef = useRef<{
        supplierId: string;
        supplierSearch: string;
        locationId: string;
        locationSearch: string;
    } | null>(null);
    const [form, setForm] = useState<EditFormState | null>(null);
    const [savedForm, setSavedForm] = useState<EditFormState | null>(null);
    const [item, setItem] = useState<InventoryItemEntry | null>(null);
    const [editingField, setEditingField] = useState<EditableCardKey | null>(null);
    const [supplierSearch, setSupplierSearch] = useState('');
    const [debouncedSupplierSearch, setDebouncedSupplierSearch] = useState('');
    const [locationSearch, setLocationSearch] = useState('');
    const [isUnsavedOpen, setIsUnsavedOpen] = useState(false);
    const [supplierFieldError, setSupplierFieldError] = useState<string | null>(null);
    const [locationFieldError, setLocationFieldError] = useState<string | null>(null);
    const [isActivityOpen, setIsActivityOpen] = useState(false);
    const [pendingCreates, setPendingCreates] = useState<PendingStockAdjustmentCreate[]>([]);
    const [pendingDeleteIds, setPendingDeleteIds] = useState<string[]>([]);
    const [pendingNoteUpdates, setPendingNoteUpdates] =
        useState<PendingStockAdjustmentNoteUpdates>({});
    const [isSaving, setIsSaving] = useState(false);
    const [tags, setTags] = useState<Array<InventoryTag>>([]);
    const [savedTagIds, setSavedTagIds] = useState<Array<string>>([]);
    const [bins, setBins] = useState<Array<InventoryBin>>([]);
    const [savedBinIds, setSavedBinIds] = useState<Array<string>>([]);
    const [partNumberRows, setPartNumberRows] = useState<Array<PartNumberEditorRow>>([]);
    const [savedPartNumbersFingerprint, setSavedPartNumbersFingerprint] = useState('');
    const [partNumbersError, setPartNumbersError] = useState<string | undefined>();
    const [partNumbersEditorOpen, setPartNumbersEditorOpen] = useState(false);
    /** Rows edited inside the Part Numbers modal; applied to `partNumberRows` on Save. */
    const [partNumbersDraft, setPartNumbersDraft] = useState<Array<PartNumberEditorRow>>([]);
    /** undefined = unchanged; null = clear; string = new S3 key */
    const [pendingImageKey, setPendingImageKey] = useState<string | null | undefined>(undefined);
    const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
    const uploadImageMutation = useUploadInventoryImage();
    const createTagMutation = useCreateInventoryTag();
    const createBinMutation = useCreateInventoryBin();
    const { isOpen: isDetailsOpen, onToggle: onDetailsToggle, onClose: onDetailsClose } =
        useDisclosure();

    const { data, isLoading: isItemLoading, isError } = useGetInventoryItem(
        inventoryItemId ?? undefined,
        isOpen && !!inventoryItemId,
    );

    const { data: adjustmentsData, isLoading: isAdjustmentsLoading } = useGetInventoryStockAdjustments(
        inventoryItemId ?? undefined,
        STOCK_ADJUSTMENTS_COLLAPSED_LIMIT,
        0,
        isOpen && !!inventoryItemId,
    );

    const isLoading = isItemLoading || isAdjustmentsLoading;

    const { data: locationsData } = useGetLocations(true);
    const { data: supplierData, isLoading: isSupplierLoading } =
        useSearchSuppliers(debouncedSupplierSearch);

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSupplierSearch(supplierSearch), 300);
        return () => clearTimeout(timer);
    }, [supplierSearch]);

    const locationOptions: TypeaheadOption[] = useMemo(() => {
        const locs = filterLocations(locationsData?.locations ?? [], { excludeAll: true });
        return locs.map((loc) => ({ value: loc._id as string, label: loc.title }));
    }, [locationsData?.locations]);

    const supplierOptions: TypeaheadOption[] = useMemo(
        () =>
            supplierData?.suppliers?.map((s) => ({
                value: s._id,
                label: s.title,
            })) ?? [],
        [supplierData?.suppliers],
    );

    const supplierDisplay = useMemo(() => {
        const match = supplierOptions.find((o) => o.value === form?.supplierId);
        if (match) return match.label;
        if (item && item.supplierId === form?.supplierId) return item.supplierName;
        return supplierSearch;
    }, [supplierOptions, form?.supplierId, item, supplierSearch]);

    const locationDisplay = useMemo(() => {
        const match = locationOptions.find((o) => o.value === form?.locationId);
        if (match) return match.label;
        if (item && item.locationId === form?.locationId) return item.locationTitle;
        return locationSearch;
    }, [locationOptions, form?.locationId, item, locationSearch]);

    /** Options for the Location dropdown; keeps the saved location visible even
     * before the locations query lands (falls back to the item's title). */
    const locationDropdownOptions = useMemo(() => {
        const opts = locationOptions.map((o) => ({ value: o.value, label: o.label }));
        if (
            form?.locationId &&
            !locationOptions.some((o) => o.value === form.locationId) &&
            locationDisplay
        ) {
            opts.unshift({ value: form.locationId, label: locationDisplay });
        }
        return opts;
    }, [locationOptions, form?.locationId, locationDisplay]);

    const updateMutation = useUpdateInventoryItem();
    const createAdjustmentMutation = useCreateInventoryStockAdjustment();
    const deleteAdjustmentMutation = useDeleteInventoryStockAdjustment();
    const updateAdjustmentNoteMutation = useUpdateInventoryStockAdjustmentNote();

    useEffect(() => {
        if (data?.item) {
            const nextForm = itemToForm(data.item);
            const nextTags = data.item.tags ?? [];
            const nextBins = data.item.bins ?? [];
            const nextPartRows = equivalentPartNumbersToEditorRows(
                data.item.equivalentPartNumbers ?? [],
                data.item.strippedManufacturerPartNumber,
            );
            setItem(data.item);
            setForm(nextForm);
            setSavedForm(nextForm);
            setTags(nextTags);
            setSavedTagIds(nextTags.map((t) => t._id));
            setBins(nextBins);
            setSavedBinIds(nextBins.map((b) => b._id));
            setPartNumberRows(nextPartRows);
            setSavedPartNumbersFingerprint(editorRowsFingerprint(nextPartRows));
            setPartNumbersError(undefined);
            setPendingImageKey(undefined);
            setImagePreviewUrl((prev) => {
                if (prev?.startsWith('blob:')) URL.revokeObjectURL(prev);
                return data.item.imageUrl ?? null;
            });
            setSupplierSearch(data.item.supplierName);
            setLocationSearch(data.item.locationTitle);
        }
    }, [data?.item]);

    useEffect(() => {
        if (!isOpen) {
            setForm(null);
            setSavedForm(null);
            setItem(null);
            setEditingField(null);
            setSupplierFieldError(null);
            setLocationFieldError(null);
            setIsActivityOpen(false);
            setPendingCreates([]);
            setPendingDeleteIds([]);
            setPendingNoteUpdates({});
            setTags([]);
            setSavedTagIds([]);
            setBins([]);
            setSavedBinIds([]);
            setPartNumberRows([]);
            setSavedPartNumbersFingerprint('');
            setPartNumbersError(undefined);
            setPartNumbersEditorOpen(false);
            setPartNumbersDraft([]);
            setPendingImageKey(undefined);
            setImagePreviewUrl((prev) => {
                if (prev?.startsWith('blob:')) URL.revokeObjectURL(prev);
                return null;
            });
            onDetailsClose();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    useEffect(() => {
        const el = bodyRef.current;
        if (!el) return;
        const onWheel = (e: WheelEvent) => {
            if (blockScrollRef.current) e.preventDefault();
        };
        el.addEventListener('wheel', onWheel, { passive: false });
        return () => el.removeEventListener('wheel', onWheel);
    }, [isOpen]);

    const tagsDirty = useMemo(() => {
        const current = tags.map((t) => t._id).slice().sort().join(',');
        const saved = savedTagIds.slice().sort().join(',');
        return current !== saved;
    }, [tags, savedTagIds]);

    const binsDirty = useMemo(() => {
        const current = bins.map((b) => b._id).slice().sort().join(',');
        const saved = savedBinIds.slice().sort().join(',');
        return current !== saved;
    }, [bins, savedBinIds]);

    const partNumbersDirty = useMemo(() => {
        return editorRowsFingerprint(partNumberRows) !== savedPartNumbersFingerprint;
    }, [partNumberRows, savedPartNumbersFingerprint]);

    const partNumbersDraftDirty =
        partNumbersEditorOpen &&
        editorRowsFingerprint(partNumbersDraft) !== editorRowsFingerprint(partNumberRows);

    const openPartNumbersEditor = () => {
        setPartNumbersDraft(partNumberRows);
        setPartNumbersError(undefined);
        setPartNumbersEditorOpen(true);
    };

    const cancelPartNumbersEditor = () => {
        setPartNumbersDraft(partNumberRows);
        setPartNumbersError(undefined);
        setPartNumbersEditorOpen(false);
    };

    const savePartNumbersEditor = () => {
        if (!normalizePartNumberRows(partNumbersDraft)) {
            setPartNumbersError('Add at least one unique part number.');
            return;
        }
        const nextRows = partNumbersDraft.filter((row) => formatPartNumber(row.value));
        if (!nextRows.some((row) => row.isPrimary)) {
            nextRows[0] = { ...nextRows[0], isPrimary: true };
        }
        setPartNumberRows(nextRows);
        setPartNumbersError(undefined);
        setPartNumbersEditorOpen(false);
    };

    const imageDirty = pendingImageKey !== undefined;

    const isFormDirty = useMemo(() => {
        if (!form || !savedForm) return false;
        return !formsEqual(form, savedForm);
    }, [form, savedForm]);

    const isDirty =
        isFormDirty ||
        tagsDirty ||
        binsDirty ||
        partNumbersDirty ||
        imageDirty ||
        pendingCreates.length > 0 ||
        pendingDeleteIds.length > 0 ||
        Object.keys(pendingNoteUpdates).length > 0;

    const discardAllDrafts = () => {
        if (savedForm && item) {
            setForm(savedForm);
            setEditingField(null);
            setSupplierFieldError(null);
            setLocationFieldError(null);
            setSupplierSearch(item.supplierName);
            setLocationSearch(item.locationTitle);
            setTags(item.tags ?? []);
            setSavedTagIds((item.tags ?? []).map((t) => t._id));
            setBins(item.bins ?? []);
            setSavedBinIds((item.bins ?? []).map((b) => b._id));
            const nextPartRows = equivalentPartNumbersToEditorRows(
                item.equivalentPartNumbers ?? [],
                item.strippedManufacturerPartNumber,
            );
            setPartNumberRows(nextPartRows);
            setSavedPartNumbersFingerprint(editorRowsFingerprint(nextPartRows));
            setPartNumbersError(undefined);
            setPendingImageKey(undefined);
            setImagePreviewUrl((prev) => {
                if (prev?.startsWith('blob:')) URL.revokeObjectURL(prev);
                return item.imageUrl ?? null;
            });
        }
        setPendingCreates([]);
        setPendingDeleteIds([]);
        setPendingNoteUpdates({});
    };

    const handleCloseAttempt = () => {
        if (isDirty) {
            setIsUnsavedOpen(true);
        } else {
            onClose();
        }
    };

    const handleCancel = () => {
        if (isDirty) {
            discardAllDrafts();
        }
        onClose();
    };

    const invalidateRelated = async () => {
        await Promise.all([
            queryClient.invalidateQueries({ queryKey: ['/inventory/feed'] }),
            queryClient.invalidateQueries({ queryKey: ['/inventory/summary'] }),
            queryClient.invalidateQueries({ queryKey: ['/inventory/detail', inventoryItemId] }),
            queryClient.invalidateQueries({ queryKey: ['/inventory/activity-logs'] }),
            queryClient.invalidateQueries({
                queryKey: ['/inventory/adjustments/feed', inventoryItemId],
            }),
        ]);
    };

    const handleSave = async () => {
        if (!form || !inventoryItemId || !savedForm) return;

        if (!form.supplierId) {
            setSupplierFieldError('Select a supplier from the list.');
            setEditingField('supplierId');
            toast({
                title: 'Supplier required',
                description: 'Select a supplier from the list.',
                status: 'warning',
                duration: 4000,
                isClosable: true,
            });
            return;
        }

        if (!form.locationId) {
            setLocationFieldError('Select a location from the list.');
            setEditingField('locationId');
            if (!isDetailsOpen) onDetailsToggle();
            toast({
                title: 'Location required',
                description: 'Select a location from the list.',
                status: 'warning',
                duration: 4000,
                isClosable: true,
            });
            return;
        }

        if (form.restockAlert) {
            if (form.reorderQuantity == null || form.targetQuantity == null) {
                toast({
                    title: 'Restock quantities required',
                    description:
                        'Reorder and target quantities are required when restock alert is on.',
                    status: 'warning',
                    duration: 4000,
                    isClosable: true,
                });
                return;
            }
        }

        const changes = buildChanges(form, savedForm);
        if (pendingImageKey !== undefined) {
            changes.imageKey = pendingImageKey;
        }

        if (partNumbersDirty) {
            const normalized = normalizePartNumberRows(partNumberRows);
            if (!normalized) {
                setPartNumbersError('Add at least one unique part number.');
                toast({
                    title: 'Invalid part numbers',
                    description: 'Add at least one unique part number with a primary selected.',
                    status: 'warning',
                    duration: 4000,
                    isClosable: true,
                });
                return;
            }
            if (!item || !equivalentPartNumbersEqual(normalized, item)) {
                changes.equivalentPartNumbers = normalized.equivalentPartNumbers;
                changes.strippedManufacturerPartNumber =
                    normalized.strippedManufacturerPartNumber;
            }
            setPartNumbersError(undefined);
        }

        if (
            Object.keys(changes).length === 0 &&
            !tagsDirty &&
            !binsDirty &&
            pendingCreates.length === 0 &&
            pendingDeleteIds.length === 0 &&
            Object.keys(pendingNoteUpdates).length === 0
        ) {
            return;
        }

        setIsSaving(true);
        try {
            if (tagsDirty) {
                const resolvedTagIds: string[] = [];
                for (const t of tags) {
                    if (t.isNew) {
                        const { tag } = await createTagMutation.mutateAsync({
                            name: t.name,
                        });
                        resolvedTagIds.push(tag._id);
                    } else {
                        resolvedTagIds.push(t._id);
                    }
                }
                changes.tagIds = resolvedTagIds;
            }

            if (binsDirty) {
                const resolvedBinIds: string[] = [];
                for (const b of bins) {
                    if (b.isNew) {
                        const { bin } = await createBinMutation.mutateAsync({
                            locationId: form.locationId,
                            name: b.name,
                        });
                        resolvedBinIds.push(bin._id);
                    } else {
                        resolvedBinIds.push(b._id);
                    }
                }
                changes.binIds = resolvedBinIds;
            }

            let latestItem = item;
            if (Object.keys(changes).length > 0) {
                const result = await updateMutation.mutateAsync({
                    inventoryItemId,
                    changes,
                });
                latestItem = result.item;
            }

            for (const draft of pendingCreates) {
                await createAdjustmentMutation.mutateAsync({
                    inventoryItemId,
                    type: draft.type,
                    quantity: draft.quantity,
                    businessLine: draft.businessLine,
                    ...(draft.roNumber ? { roNumber: draft.roNumber } : {}),
                    ...(draft.unitCost != null ? { unitCost: draft.unitCost } : {}),
                    ...(draft.note ? { note: draft.note } : {}),
                });
            }

            for (const [adjustmentId, note] of Object.entries(pendingNoteUpdates)) {
                if (pendingDeleteIds.includes(adjustmentId)) continue;
                await updateAdjustmentNoteMutation.mutateAsync({
                    inventoryItemId,
                    adjustmentId,
                    note,
                });
            }

            for (const adjustmentId of pendingDeleteIds) {
                await deleteAdjustmentMutation.mutateAsync({
                    inventoryItemId,
                    adjustmentId,
                });
            }

            // Item field updates return a full item; stock create/delete do not — list/detail
            // refresh via invalidateRelated below (drawer closes after save).
            if (Object.keys(changes).length > 0 && latestItem) {
                const nextForm = itemToForm(latestItem);
                const nextTags = latestItem.tags ?? [];
                const nextBins = latestItem.bins ?? [];
                const nextPartRows = equivalentPartNumbersToEditorRows(
                    latestItem.equivalentPartNumbers ?? [],
                    latestItem.strippedManufacturerPartNumber,
                );
                const nextImageUrl = latestItem.imageUrl ?? null;
                setItem(latestItem);
                setForm(nextForm);
                setSavedForm(nextForm);
                setTags(nextTags);
                setSavedTagIds(nextTags.map((t) => t._id));
                setBins(nextBins);
                setSavedBinIds(nextBins.map((b) => b._id));
                setPartNumberRows(nextPartRows);
                setSavedPartNumbersFingerprint(editorRowsFingerprint(nextPartRows));
                setPartNumbersError(undefined);
                setPendingImageKey(undefined);
                setImagePreviewUrl((prev) => {
                    if (prev?.startsWith('blob:')) URL.revokeObjectURL(prev);
                    return nextImageUrl;
                });
                setLocationSearch(latestItem.locationTitle);
                setSupplierSearch(latestItem.supplierName);
            }
            setEditingField(null);
            setPendingCreates([]);
            setPendingDeleteIds([]);
            setPendingNoteUpdates({});
            await invalidateRelated();
            toast({
                title: 'Changes saved',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
            onClose();
        } catch (error: unknown) {
            const referenceError = parseInventoryReferenceValidationError(error);
            if (referenceError?.field === 'supplierId') {
                setSupplierFieldError(referenceError.message);
                setEditingField('supplierId');
                return;
            }
            if (referenceError?.field === 'locationId') {
                setLocationFieldError(referenceError.message);
                setEditingField('locationId');
                if (!isDetailsOpen) onDetailsToggle();
                return;
            }
            const duplicate = parseInventoryDuplicateError(error);
            if (duplicate) {
                toast({
                    title: 'Duplicate item',
                    description: duplicate.message,
                    status: 'warning',
                    duration: 6000,
                    isClosable: true,
                });
                return;
            }
            const message = (error as { response?: { data?: { message?: string; }; }; })?.response
                ?.data?.message;
            toast({
                title: 'Failed to save changes',
                description: message ?? 'Please try again.',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setIsSaving(false);
        }
    };

    const updateField = <K extends keyof EditFormState>(key: K, value: EditFormState[K]) => {
        setForm((prev) => {
            if (!prev) return prev;
            if (key === 'locationId' && prev.locationId !== value) {
                setBins([]);
            }
            return { ...prev, [key]: value };
        });
    };

    const beginFieldEdit = (field: EditableCardKey) => {
        preEditFieldRef.current = {
            supplierId: form?.supplierId ?? '',
            supplierSearch,
            locationId: form?.locationId ?? '',
            locationSearch,
        };
        setEditingField(field);
    };

    const categoryLabel =
        INVENTORY_CATEGORY_OPTIONS.find((o) => o.value === item?.category)?.label ??
        item?.category ??
        '';

    const handlePrintBarcode = () => {
        if (!item?.barcodeImageUrl) {
            toast({
                title: 'Barcode unavailable',
                description: 'No barcode image is available for this item.',
                status: 'warning',
                duration: 4000,
                isClosable: true,
            });
            return;
        }

        const existing = document.getElementById('inventory-barcode-print-frame');
        if (existing) existing.remove();

        const iframe = document.createElement('iframe');
        iframe.id = 'inventory-barcode-print-frame';
        iframe.setAttribute('aria-hidden', 'true');
        iframe.style.cssText =
            'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;';
        document.body.appendChild(iframe);

        const frameWindow = iframe.contentWindow;
        const frameDoc = frameWindow?.document;
        if (!frameWindow || !frameDoc) {
            iframe.remove();
            toast({
                title: 'Print failed',
                description: 'Could not open the barcode print dialog.',
                status: 'error',
                duration: 4000,
                isClosable: true,
            });
            return;
        }

        const cleanup = () => {
            iframe.remove();
        };

        frameDoc.open();
        frameDoc.write(`<!doctype html><html><head><title>Barcode</title>
<style>html,body{margin:0;padding:0;}img{max-width:100%;}</style></head><body>
<img src="${item.barcodeImageUrl}" alt="Barcode" />
</body></html>`);
        frameDoc.close();

        const img = frameDoc.querySelector('img');
        const triggerPrint = () => {
            try {
                frameWindow.focus();
                frameWindow.print();
            } finally {
                // Remove after the print dialog is dismissed (or shortly if afterprint unsupported).
                frameWindow.addEventListener('afterprint', cleanup, { once: true });
                window.setTimeout(cleanup, 60_000);
            }
        };

        if (img) {
            if (img.complete) {
                triggerPrint();
            } else {
                img.onload = triggerPrint;
                img.onerror = () => {
                    cleanup();
                    toast({
                        title: 'Barcode unavailable',
                        description: 'Could not load the barcode image for printing.',
                        status: 'error',
                        duration: 4000,
                        isClosable: true,
                    });
                };
            }
        } else {
            cleanup();
        }
    };

    const primaryPartNumber = item
        ? getPrimaryFromEditorRows(partNumberRows) || getPrimaryPartNumber(item)
        : '';
    const alternatePartNumberCount = Math.max(
        0,
        partNumberRows.filter((r) => r.value.trim()).length - 1,
    );

    const isLowStockPreview =
        !!form &&
        !!item &&
        form.restockAlert &&
        form.reorderQuantity != null &&
        form.status === 'active' &&
        item.inStockQuantity <= form.reorderQuantity;

    const unitLabel = item ? formatInventoryUnitLabel(item.unit) : '';

    const lastSavedLabel = useMemo(() => {
        if (!item?.updatedAt) return null;
        const savedAt = moment(item.updatedAt);
        if (!savedAt.isValid()) return null;
        const savedAtMs = savedAt.valueOf();
        const matchingAdjustment = adjustmentsData?.adjustments?.find(
            (adj) => new Date(adj.timestamp).getTime() === savedAtMs,
        );
        const actor = matchingAdjustment?.userName
            ? `${matchingAdjustment.userName.firstName} ${matchingAdjustment.userName.lastName}`.trim()
            : '';
        return `Last saved ${savedAt.format('MMM D, h:mm A')}${actor ? ` by ${actor}` : ''}`;
    }, [item?.updatedAt, adjustmentsData?.adjustments]);

    const headerIconButtonProps = {
        size: 'sm',
        variant: 'ghost',
        colorScheme: 'gray',
        color: 'fg.muted',
        _hover: { bg: 'bg.muted' },
    } as const;

    return (
        <>
            <Drawer isOpen={isOpen} onClose={handleCloseAttempt} placement="right" size="full">
                <DrawerOverlay />
                <DrawerContent
                    data-partbay-drawer=""
                    overflow="hidden"
                    bg="white"
                    borderLeftWidth="1px"
                    borderLeftColor="border.subtle"
                    maxW={{
                        base: '100%',
                        lg: isActivityOpen
                            ? EDIT_DRAWER_WITH_ACTIVITY_MAX_W
                            : EDIT_DRAWER_BASE_MAX_W,
                    }}
                    transition="max-width 0.2s ease"
                >
                    <Flex h="100%" position="relative">
                        <Box flex="1" display="flex" flexDirection="column" overflow="hidden" minW={0}>
                            {form && item && !isLoading && !isError ? (
                                <Box
                                    bg="white"
                                    px={6}
                                    pt={5}
                                    pb={4}
                                    flexShrink={0}
                                    onWheel={(e) => {
                                        const target = e.target as HTMLElement;
                                        // Never yank the section closed under an open control:
                                        // portaled popovers (Location, Bins) bubble wheel events
                                        // through the React tree, and scrolling over the details'
                                        // own fields is interaction, not a dismissal gesture.
                                        if (detailsRef.current?.contains(target)) return;
                                        if (
                                            target.closest(
                                                '.chakra-popover__content, [data-inventory-floating-menu]',
                                            )
                                        ) {
                                            return;
                                        }
                                        if (e.deltaY > 0 && isDetailsOpen) {
                                            blockScrollRef.current = true;
                                            onDetailsClose();
                                            window.setTimeout(() => {
                                                blockScrollRef.current = false;
                                            }, 300);
                                        }
                                    }}
                                >
                                    <Flex align="flex-start" gap={5}>
                                        <Box w={IMAGE_TILE_SIDE} flexShrink={0}>
                                            <InventoryItemImageField
                                                variant="tile"
                                                alt={form.name}
                                                previewUrl={imagePreviewUrl}
                                                isUploading={uploadImageMutation.isPending}
                                                cropTitle={`${form.name.trim() || '—'} · ${getPrimaryPartNumber(item) || '—'}`}
                                                onFileSelected={async (file) => {
                                                    const objectUrl = URL.createObjectURL(file);
                                                    setImagePreviewUrl((prev) => {
                                                        if (prev?.startsWith('blob:')) {
                                                            URL.revokeObjectURL(prev);
                                                        }
                                                        return objectUrl;
                                                    });
                                                    try {
                                                        const key =
                                                            await uploadImageMutation.mutateAsync(file);
                                                        setPendingImageKey(key);
                                                    } catch {
                                                        setImagePreviewUrl((prev) => {
                                                            if (prev?.startsWith('blob:')) {
                                                                URL.revokeObjectURL(prev);
                                                            }
                                                            return item.imageUrl ?? null;
                                                        });
                                                        setPendingImageKey(undefined);
                                                        toast({
                                                            title: 'Image upload failed',
                                                            status: 'error',
                                                            duration: 4000,
                                                            isClosable: true,
                                                        });
                                                    }
                                                }}
                                                onClear={() => {
                                                    setImagePreviewUrl((prev) => {
                                                        if (prev?.startsWith('blob:')) {
                                                            URL.revokeObjectURL(prev);
                                                        }
                                                        return null;
                                                    });
                                                    setPendingImageKey(null);
                                                }}
                                            />
                                        </Box>

                                        <Box flex={1} minW={0}>
                                            <Flex align="center" justify="space-between" gap={3}>
                                                <StatusBadgeMenu
                                                    value={form.status}
                                                    inStockQuantity={item.inStockQuantity}
                                                    isFlagged={isLowStockPreview}
                                                    onChange={(status) =>
                                                        updateField('status', status)
                                                    }
                                                    onDelete={
                                                        onDelete
                                                            ? () => onDelete(item)
                                                            : undefined
                                                    }
                                                />
                                                <HStack spacing={1} flexShrink={0}>
                                                    <Tooltip label="Print barcode" placement="bottom">
                                                        <IconButton
                                                            aria-label="Print barcode"
                                                            icon={<Icon name="printer" size="lg" />}
                                                            {...headerIconButtonProps}
                                                            isDisabled={!item.barcodeImageUrl}
                                                            onClick={handlePrintBarcode}
                                                        />
                                                    </Tooltip>
                                                    <Tooltip label="Item activity" placement="bottom">
                                                        <IconButton
                                                            aria-label="View activity"
                                                            icon={<Icon name="history" size="lg" />}
                                                            {...headerIconButtonProps}
                                                            color={isActivityOpen ? 'brand.fg' : 'fg.muted'}
                                                            bg={isActivityOpen ? 'brand.subtle' : 'transparent'}
                                                            _hover={{
                                                                bg: isActivityOpen
                                                                    ? 'brand.muted'
                                                                    : 'bg.muted',
                                                            }}
                                                            onClick={() =>
                                                                setIsActivityOpen((prev) => !prev)
                                                            }
                                                        />
                                                    </Tooltip>
                                                    <Box
                                                        w="1px"
                                                        h="20px"
                                                        bg="border.subtle"
                                                        mx={1}
                                                        flexShrink={0}
                                                    />
                                                    <IconButton
                                                        aria-label="Close"
                                                        icon={<Icon name="x" size="lg" />}
                                                        {...headerIconButtonProps}
                                                        onClick={handleCloseAttempt}
                                                    />
                                                </HStack>
                                            </Flex>

                                            <Flex align="center" gap={2} mt={2} minW={0}>
                                                <Box
                                                    display="inline-grid"
                                                    minW={0}
                                                    maxW="100%"
                                                    textStyle="h4"
                                                    fontWeight="bold"
                                                    textTransform="uppercase"
                                                    color="fg.default"
                                                    sx={{ '& > *': { gridArea: '1 / 1' } }}
                                                >
                                                    {editingField === 'name' ? (
                                                        <>
                                                            <Box
                                                                as="span"
                                                                visibility="hidden"
                                                                whiteSpace="pre"
                                                                overflow="hidden"
                                                                aria-hidden
                                                            >
                                                                {form.name || ' '}
                                                            </Box>
                                                            <Input
                                                                variant="unstyled"
                                                                value={form.name}
                                                                aria-label="Item name"
                                                                autoFocus
                                                                h="auto"
                                                                w="100%"
                                                                minW="1ch"
                                                                p={0}
                                                                borderRadius={0}
                                                                sx={{
                                                                    font: 'inherit',
                                                                    letterSpacing: 'inherit',
                                                                    textTransform: 'inherit',
                                                                    color: 'inherit',
                                                                }}
                                                                onChange={(e) =>
                                                                    updateField('name', e.target.value)
                                                                }
                                                                onBlur={() => setEditingField(null)}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === 'Enter') {
                                                                        setEditingField(null);
                                                                    }
                                                                }}
                                                            />
                                                        </>
                                                    ) : (
                                                        <Text
                                                            as="span"
                                                            noOfLines={1}
                                                            title={form.name}
                                                            minW={0}
                                                            cursor="text"
                                                            onClick={() => setEditingField('name')}
                                                        >
                                                            {form.name}
                                                        </Text>
                                                    )}
                                                </Box>
                                                <Tooltip label="Edit name" placement="top">
                                                    <IconButton
                                                        aria-label="Edit name"
                                                        icon={<Icon name="pencil" size="sm" />}
                                                        size="xs"
                                                        variant="ghost"
                                                        colorScheme="gray"
                                                        color={
                                                            editingField === 'name'
                                                                ? 'brand.fg'
                                                                : 'fg.muted'
                                                        }
                                                        flexShrink={0}
                                                        onMouseDown={(e) => e.preventDefault()}
                                                        onClick={() => setEditingField('name')}
                                                    />
                                                </Tooltip>
                                            </Flex>

                                            <Flex align="center" gap={3} mt={3} minW={0}>
                                                <Flex
                                                    align="center"
                                                    gap={3}
                                                    flexWrap="wrap"
                                                    flex={1}
                                                    minW={0}
                                                >
                                                    <HStack
                                                        spacing={1}
                                                        h="32px"
                                                        pl={2.5}
                                                        pr={1}
                                                        borderWidth="1px"
                                                        borderColor="border.default"
                                                        borderRadius="lg"
                                                        flexShrink={0}
                                                        data-table-mfg-part
                                                    >
                                                        <Text {...statLabelProps} mr={1}>
                                                            MPN
                                                        </Text>
                                                        <Text
                                                            fontSize="sm"
                                                            fontWeight="semibold"
                                                            fontFamily="mono"
                                                            color="fg.default"
                                                        >
                                                            {primaryPartNumber}
                                                        </Text>
                                                        {alternatePartNumberCount > 0 && (
                                                            <Text
                                                                fontSize="xs"
                                                                fontWeight="medium"
                                                                color="fg.muted"
                                                            >
                                                                +{alternatePartNumberCount}
                                                            </Text>
                                                        )}
                                                        <Box
                                                            w="1px"
                                                            h="16px"
                                                            bg="border.subtle"
                                                            mx={1}
                                                        />
                                                        <CopyButton
                                                            text={primaryPartNumber}
                                                            label="Copy mfg. part number"
                                                            color="fg.muted"
                                                            _hover={{ color: 'fg.default', bg: 'bg.subtle' }}
                                                            minW="auto"
                                                            h="auto"
                                                            p={1}
                                                        />
                                                        <Tooltip label="Edit part numbers" placement="top">
                                                            <IconButton
                                                                aria-label="Edit part numbers"
                                                                icon={<Icon name="pencil" size="xs" />}
                                                                size="xs"
                                                                variant="ghost"
                                                                colorScheme="gray"
                                                                color="fg.muted"
                                                                minW="auto"
                                                                h="auto"
                                                                p={1}
                                                                onClick={openPartNumbersEditor}
                                                            />
                                                        </Tooltip>
                                                    </HStack>
                                                    <Box minW={0}>
                                                        <InventoryTagsField
                                                            value={tags}
                                                            onChange={setTags}
                                                            label=""
                                                            compact
                                                        />
                                                    </Box>
                                                </Flex>
                                                <Flex
                                                    as="button"
                                                    type="button"
                                                    align="center"
                                                    flexShrink={0}
                                                    color="brand.fg"
                                                    fontSize="sm"
                                                    fontWeight="semibold"
                                                    onClick={onDetailsToggle}
                                                    aria-expanded={isDetailsOpen}
                                                >
                                                    {isDetailsOpen ? 'Show less' : 'Show more'}
                                                    <Icon
                                                        name={isDetailsOpen ? 'chevronUp' : 'chevronDown'}
                                                        size="sm"
                                                        ml={1}
                                                    />
                                                </Flex>
                                            </Flex>
                                        </Box>
                                    </Flex>

                                    <Collapse in={isDetailsOpen} animateOpacity>
                                        <Box
                                            ref={detailsRef}
                                            mt={4}
                                            p={4}
                                            bg="bg.surface"
                                            borderWidth="1px"
                                            borderColor="border.subtle"
                                            borderRadius="xl"
                                        >
                                            <Grid
                                                templateColumns={{
                                                    base: 'repeat(2, 1fr)',
                                                    md: 'repeat(4, 1fr)',
                                                }}
                                                columnGap={4}
                                                rowGap={3}
                                                mb={4}
                                            >
                                                <DetailField
                                                    label="Category"
                                                    value={categoryLabel || '—'}
                                                />
                                                <DetailField
                                                    label="Part Type"
                                                    value={
                                                        item.partType
                                                            ? formatInventoryPartTypeLabel(item.partType)
                                                            : '—'
                                                    }
                                                />
                                                <DetailField
                                                    label="Unit"
                                                    value={unitLabel || '—'}
                                                />
                                                <DetailField
                                                    label="Origin"
                                                    value={item.origin?.trim() || '—'}
                                                />
                                            </Grid>

                                            <Grid
                                                templateColumns={{ base: '1fr', sm: 'repeat(2, 1fr)' }}
                                                columnGap={4}
                                                rowGap={3}
                                            >
                                                <Box>
                                                    <Text {...statLabelProps} mb={1}>
                                                        Location
                                                    </Text>
                                                    <Dropdown
                                                        options={locationDropdownOptions}
                                                        value={form.locationId || null}
                                                        onChange={(value) => {
                                                            updateField('locationId', value);
                                                            const match = locationOptions.find(
                                                                (o) => o.value === value,
                                                            );
                                                            if (match) setLocationSearch(match.label);
                                                            setLocationFieldError(null);
                                                        }}
                                                        searchable
                                                        placeholder="Select location"
                                                    />
                                                    {locationFieldError && (
                                                        <Text fontSize="sm" color="error.fg" mt={1}>
                                                            {locationFieldError}
                                                        </Text>
                                                    )}
                                                </Box>
                                                <Box>
                                                    <InventoryBinsField
                                                        value={bins}
                                                        onChange={setBins}
                                                        locationId={form?.locationId || null}
                                                        isDisabled={!form?.locationId}
                                                        placeholder={
                                                            form?.locationId
                                                                ? 'Search bins…'
                                                                : 'Select a location first'
                                                        }
                                                        labelProps={{
                                                            ...statLabelProps,
                                                            // Match the Location label's Text mb so the
                                                            // two triggers sit on the same line.
                                                            mb: 1,
                                                        }}
                                                    />
                                                </Box>
                                            </Grid>
                                        </Box>
                                    </Collapse>
                                </Box>
                            ) : (
                                <Flex
                                    align="center"
                                    justify="flex-end"
                                    px={6}
                                    h="topbar"
                                    flexShrink={0}
                                    bg="white"
                                    borderBottom="1px solid"
                                    borderColor="border.subtle"
                                >
                                    <CloseButton onClick={handleCloseAttempt} size="sm" />
                                </Flex>
                            )}

                            <Box ref={bodyRef} flex={1} overflowY="auto" px={0} py={0}>
                                {isLoading && (
                                    <Flex justify="center" align="center" h="300px">
                                        <Spinner color="brand.solid" />
                                    </Flex>
                                )}
                                {isError && (
                                    <Text color="error.fg" textAlign="center" py={8}>
                                        Failed to load inventory item
                                    </Text>
                                )}
                                {!isLoading && !isError && form && item && (
                                    <Box px={6} pb={6}>
                                        <Grid
                                            templateColumns={{
                                                base: 'repeat(2, 1fr)',
                                                md: 'repeat(4, 1fr)',
                                            }}
                                            borderTopWidth="1px"
                                            borderBottomWidth="1px"
                                            borderColor="border.subtle"
                                            mx={-6}
                                            px={6}
                                        >
                                            <StatCell label="In Stock" index={0}>
                                                <HStack spacing={1.5} align="baseline">
                                                    <Text
                                                        fontSize="2xl"
                                                        fontWeight="bold"
                                                        color="fg.default"
                                                        lineHeight="short"
                                                    >
                                                        {item.inStockQuantity}
                                                    </Text>
                                                    <Text fontSize="sm" color="fg.default">
                                                        {unitLabel}
                                                    </Text>
                                                </HStack>
                                            </StatCell>
                                            <StatCell label="Avg Unit Cost" index={1}>
                                                <Text
                                                    fontSize="2xl"
                                                    fontWeight="bold"
                                                    color="fg.default"
                                                    lineHeight="short"
                                                >
                                                    {formatPrice(item.averageUnitCost)}
                                                </Text>
                                            </StatCell>
                                            <StatCell label="Stock Value" index={2}>
                                                <Text
                                                    fontSize="2xl"
                                                    fontWeight="bold"
                                                    color="fg.default"
                                                    lineHeight="short"
                                                >
                                                    {formatPrice(
                                                        item.inStockQuantity * item.averageUnitCost,
                                                    )}
                                                </Text>
                                            </StatCell>
                                            <StatCell
                                                label="Supplier"
                                                index={3}
                                                cellRef={supplierCardRef}
                                                onEdit={() => beginFieldEdit('supplierId')}
                                            >
                                                {editingField === 'supplierId' ? (
                                                    <FormControl isInvalid={!!supplierFieldError}>
                                                        <InventoryTypeaheadField
                                                            value={supplierDisplay}
                                                            selectedLabel={supplierDisplay}
                                                            onChange={(val) => {
                                                                const match =
                                                                    supplierOptions.find(
                                                                        (o) => o.value === val,
                                                                    ) ??
                                                                    supplierOptions.find(
                                                                        (o) => o.label === val,
                                                                    );
                                                                if (match) {
                                                                    updateField(
                                                                        'supplierId',
                                                                        match.value,
                                                                    );
                                                                    setSupplierSearch(match.label);
                                                                } else {
                                                                    updateField('supplierId', '');
                                                                    setSupplierSearch(val);
                                                                }
                                                                setSupplierFieldError(null);
                                                            }}
                                                            options={supplierOptions}
                                                            onSearch={setSupplierSearch}
                                                            placeholder="Search suppliers…"
                                                            isLoading={isSupplierLoading}
                                                            minSearchLength={3}
                                                            inline
                                                            anchorRef={supplierCardRef}
                                                            fontSize="lg"
                                                            fontWeight="bold"
                                                            color="fg.default"
                                                            autoFocus
                                                            onBlur={(query) => {
                                                                if (
                                                                    !query.trim() &&
                                                                    preEditFieldRef.current
                                                                ) {
                                                                    updateField(
                                                                        'supplierId',
                                                                        preEditFieldRef.current
                                                                            .supplierId,
                                                                    );
                                                                    setSupplierSearch(
                                                                        preEditFieldRef.current
                                                                            .supplierSearch,
                                                                    );
                                                                    setSupplierFieldError(null);
                                                                }
                                                                setEditingField(null);
                                                            }}
                                                        />
                                                        <FormErrorMessage>
                                                            {supplierFieldError}
                                                        </FormErrorMessage>
                                                    </FormControl>
                                                ) : (
                                                    <Text
                                                        fontSize="lg"
                                                        fontWeight="bold"
                                                        color="fg.default"
                                                        lineHeight="short"
                                                        noOfLines={2}
                                                        title={supplierDisplay}
                                                    >
                                                        {supplierDisplay || '—'}
                                                    </Text>
                                                )}
                                            </StatCell>
                                        </Grid>

                                        <Flex align="center" justify="space-between" py={4} gap={3}>
                                            <HStack spacing={2} color="fg.muted">
                                                <Icon name="bell" size="sm" />
                                                <Text fontSize="sm" color="fg.muted">
                                                    Restock alert
                                                </Text>
                                                <Text
                                                    fontSize="sm"
                                                    fontWeight="semibold"
                                                    color="fg.default"
                                                >
                                                    {form.restockAlert ? 'On' : 'Off'}
                                                </Text>
                                            </HStack>
                                            <Switch
                                                aria-label="Restock alert"
                                                isChecked={form.restockAlert}
                                                onChange={(e) => {
                                                    const on = e.target.checked;
                                                    setForm((prev) => {
                                                        if (!prev) return prev;
                                                        return {
                                                            ...prev,
                                                            restockAlert: on,
                                                            ...(on
                                                                ? {
                                                                      reorderQuantity:
                                                                          prev.reorderQuantity ?? 0,
                                                                      targetQuantity:
                                                                          prev.targetQuantity ?? 0,
                                                                  }
                                                                : {}),
                                                        };
                                                    });
                                                }}
                                            />
                                        </Flex>

                                        {form.restockAlert && (
                                            <Grid
                                                templateColumns={{ base: '1fr', sm: 'repeat(2, 1fr)' }}
                                                columnGap={3}
                                                rowGap={3}
                                                mb={4}
                                            >
                                                <FormControl>
                                                    <FormLabel
                                                        fontSize="sm"
                                                        fontWeight="medium"
                                                        color="fg.default"
                                                        mb={1.5}
                                                    >
                                                        Reorder quantity
                                                    </FormLabel>
                                                    <Box position="relative">
                                                        <QuantityInputField
                                                            size="md"
                                                            value={form.reorderQuantity}
                                                            onChange={(val) =>
                                                                updateField('reorderQuantity', val)
                                                            }
                                                            allowEmpty={!form.restockAlert}
                                                        />
                                                        <Text
                                                            position="absolute"
                                                            right={3}
                                                            top="50%"
                                                            transform="translateY(-50%)"
                                                            fontSize="sm"
                                                            color="fg.muted"
                                                            pointerEvents="none"
                                                        >
                                                            {unitLabel}
                                                        </Text>
                                                    </Box>
                                                </FormControl>
                                                <FormControl>
                                                    <FormLabel
                                                        fontSize="sm"
                                                        fontWeight="medium"
                                                        color="fg.default"
                                                        mb={1.5}
                                                    >
                                                        Target quantity
                                                    </FormLabel>
                                                    <Box position="relative">
                                                        <QuantityInputField
                                                            size="md"
                                                            value={form.targetQuantity}
                                                            onChange={(val) =>
                                                                updateField('targetQuantity', val)
                                                            }
                                                            allowEmpty={!form.restockAlert}
                                                        />
                                                        <Text
                                                            position="absolute"
                                                            right={3}
                                                            top="50%"
                                                            transform="translateY(-50%)"
                                                            fontSize="sm"
                                                            color="fg.muted"
                                                            pointerEvents="none"
                                                        >
                                                            {unitLabel}
                                                        </Text>
                                                    </Box>
                                                </FormControl>
                                            </Grid>
                                        )}

                                        <Box mt={4}>
                                            <InventoryStockAdjustmentsSection
                                                inventoryItemId={inventoryItemId!}
                                                item={item}
                                                enabled={isOpen}
                                                pendingCreates={pendingCreates}
                                                pendingDeleteIds={pendingDeleteIds}
                                                pendingNoteUpdates={pendingNoteUpdates}
                                                onPendingCreatesChange={setPendingCreates}
                                                onPendingDeleteIdsChange={setPendingDeleteIds}
                                                onPendingNoteUpdatesChange={
                                                    setPendingNoteUpdates
                                                }
                                            />
                                        </Box>
                                    </Box>
                                )}
                            </Box>

                            {form && item && !isLoading && !isError && (
                                <DrawerFooter
                                    borderTopWidth="1px"
                                    borderColor="border.subtle"
                                    px={6}
                                    py={4}
                                    flexShrink={0}
                                    bg="white"
                                >
                                    <Flex w="100%" align="center" justify="space-between" gap={3}>
                                        <Text fontSize="sm" color="fg.muted" noOfLines={1} minW={0}>
                                            {lastSavedLabel}
                                        </Text>
                                        <HStack spacing={3} flexShrink={0}>
                                            <Button
                                                size="action"
                                                variant="outlineGray"
                                                onClick={handleCancel}
                                            >
                                                Cancel
                                            </Button>
                                            <Button
                                                size="action"
                                                onClick={handleSave}
                                                isLoading={isSaving || uploadImageMutation.isPending}
                                                isDisabled={!isDirty || uploadImageMutation.isPending}
                                                loadingText="Saving…"
                                            >
                                                Save
                                            </Button>
                                        </HStack>
                                    </Flex>
                                </DrawerFooter>
                            )}
                        </Box>

                        {isActivityOpen && inventoryItemId && (
                            <Box
                                w={{ base: '100%', lg: ACTIVITY_PANEL_W }}
                                flexShrink={0}
                                position={{ base: 'absolute', lg: 'static' }}
                                inset={{ base: 0, lg: 'auto' }}
                                zIndex={{ base: 2, lg: 'auto' }}
                                bg="white"
                                borderLeftWidth={{ base: 0, lg: '1px' }}
                                borderColor="border.subtle"
                            >
                                <InventoryItemActivityPanel
                                    inventoryItemId={inventoryItemId}
                                    onClose={() => setIsActivityOpen(false)}
                                    enabled={isActivityOpen}
                                />
                            </Box>
                        )}
                    </Flex>
                </DrawerContent>
            </Drawer>

            <Modal
                isOpen={partNumbersEditorOpen}
                onClose={cancelPartNumbersEditor}
                size="2xl"
                title={(() => {
                    const primary =
                        getPrimaryFromEditorRows(partNumbersDraft) ||
                        (item ? getPrimaryPartNumber(item) : '');
                    return primary ? `Part Numbers (${primary})` : 'Part Numbers';
                })()}
                footer={
                    partNumbersDraftDirty ? (
                        <>
                            <Button
                                size="action"
                                variant="outlineGray"
                                onClick={cancelPartNumbersEditor}
                            >
                                Cancel
                            </Button>
                            <Button size="action" onClick={savePartNumbersEditor}>
                                Save
                            </Button>
                        </>
                    ) : undefined
                }
            >
                <Box pb={partNumbersDraftDirty ? 0 : 2}>
                    <InventoryPartNumbersField
                        value={partNumbersDraft}
                        onChange={(rows) => {
                            setPartNumbersDraft(rows);
                            setPartNumbersError(undefined);
                        }}
                        error={partNumbersError}
                    />
                </Box>
            </Modal>

            <ConfirmDialog
                isOpen={isUnsavedOpen}
                onClose={() => setIsUnsavedOpen(false)}
                onConfirm={() => {
                    setIsUnsavedOpen(false);
                    discardAllDrafts();
                    onClose();
                }}
                title="Unsaved changes"
                confirmLabel="Discard"
                cancelLabel="Keep editing"
                confirmColorScheme="red"
            >
                You have unsaved changes. Discard them and close?
            </ConfirmDialog>
        </>
    );
};

export default EditInventoryItemDrawer;